/** sessionStorage keys holding the prev/next navigation order for the lead detail. */
const NAV_STORAGE_KEYS = [
  'pipeline_navigation_leads',
  'pipeline_navigation_context',
  'pipeline_navigation_stage_ids',
  'pipeline_navigation_stage_marker',
  'pipeline_navigation_moved_out',
];

const MOVED_OUT_KEY = 'pipeline_navigation_moved_out';

/** Leads moved out of the walked column since the agent opened it from the board. */
export function readMovedOut(): string[] {
  try {
    const ids = JSON.parse(sessionStorage.getItem(MOVED_OUT_KEY) || '[]');
    return Array.isArray(ids) ? ids : [];
  } catch { return []; }
}

export function markMovedOut(leadId: string) {
  const ids = readMovedOut();
  if (ids.includes(leadId)) return;
  try { sessionStorage.setItem(MOVED_OUT_KEY, JSON.stringify([...ids, leadId])); } catch { /* unavailable */ }
}

/** A new card click starts a new walk. */
export function resetMovedOut() {
  try { sessionStorage.removeItem(MOVED_OUT_KEY); } catch { /* unavailable */ }
}

/**
 * Drop the stored navigation order. sessionStorage survives the sign-out redirect, so
 * without this the next user in the same tab navigates by the previous user's list.
 */
export function clearProspectsNavigationState() {
  try {
    NAV_STORAGE_KEYS.forEach(key => sessionStorage.removeItem(key));
  } catch { /* sessionStorage unavailable */ }
}

export type StageNavScope = {
  email: string;
  stage?: string | null;
  myLeadsOnly?: boolean;
};

/** Identifies the scope a cached stage-ids list belongs to. */
export function stageNavMarker(scope: StageNavScope): string {
  return [scope.email, scope.stage ?? '', String(!!scope.myLeadsOnly)].join('|');
}

/** True when the stored list was fetched for this exact scope and is non-empty. */
export function isStageNavCacheFresh(
  wanted: string,
  stored: string | null,
  ids: string[] | null,
): boolean {
  return !!stored && stored === wanted && Array.isArray(ids) && ids.length > 0;
}

export type StageNavigation = {
  prevId: string | null;
  nextId: string | null;
  /** 1-based place of the open lead in the column; null once it has left the column. */
  position: number | null;
  total: number;
};

/**
 * Prev/next within the column the agent opened the lead from. A lead moved to another
 * stage drops out of that column, but the arrows keep walking it from the slot the lead
 * held: `anchor` is the last list that still contained it, so a refetched column without
 * the lead still yields its former neighbours.
 */
export function resolveStageNavigation({
  ids,
  leadId,
  movedOut,
  anchor,
  excluded = [],
  columnTotal = null,
}: {
  ids: string[];
  leadId: string;
  movedOut: boolean;
  anchor: string[] | null;
  /** Leads moved out of the column during this walk: never landed on again. */
  excluded?: string[];
  /** Column count at click time, for when `ids` is only the loaded page. */
  columnTotal?: number | null;
}): StageNavigation {
  const gone = new Set(excluded.filter(id => id !== leadId));
  if (movedOut) gone.add(leadId);
  const list = ids.filter(id => !gone.has(id));
  const total = columnTotal != null ? Math.max(list.length, columnTotal - gone.size) : list.length;
  const index = list.indexOf(leadId);
  if (index >= 0) {
    return {
      prevId: list[index - 1] ?? null,
      nextId: list[index + 1] ?? null,
      position: index + 1,
      total,
    };
  }

  const source = ids.includes(leadId) ? ids : anchor;
  const slot = source ? source.indexOf(leadId) : -1;
  if (!source || slot < 0) return { prevId: null, nextId: null, position: null, total };

  const remaining = new Set(list);
  const prevId = source.slice(0, slot).reverse().find(id => remaining.has(id)) ?? null;
  const nextId = source.slice(slot + 1).find(id => remaining.has(id)) ?? null;
  return { prevId, nextId, position: null, total };
}
